import React, { useRef, useMemo, useState } from 'react';
import { Alert, Empty, Flex, Button, theme } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';

import Integrations, { defaultIntegrationMethods } from './Integrations';
import { IntegrationSearch } from './IntegrationSearch';
import { IntegrationListToggle } from './IntegrationListToggle';
import { filterIntegrations } from './filterIntegrations';
import SpinningWheel from '../../../../../components/SpinningWheel';
import './documentation.css';
import { usePrerenderUser, usePrerenderUserId } from '../../../../../hooks/usePrerenderUser';
import { useEvent } from '../../../../events/hooks/useEvent';
import { getDocument } from './docs/kb/getDocument';
import { IntegrationStepHeader } from './IntegrationStepHeader';
import { NexusDock } from './NexusDock';
import { NexusChat } from './Nexus/NexusChat';
import { useNexusConversation } from '../../../hooks/useNexusConversation';
import { useNexusReferralHandoff } from '../../../hooks/useNexusReferralHandoff';
import { AUTOMATED_CF_INTEGRATION_FLOW, INTEGRATE_STEP_ENTRY_POINT } from '../../../nexusReferral';
import { useWizardGutter } from '../../../WizardGutter';
import {
  CloudflareBlockedStep,
  CloudflareEntryStep,
  CloudflareFlowScreens,
  CloudflareRecommendationStep,
  cfNexusOpenedProperties,
  cfStartContextOf,
  domainsProbedThisPage,
  FLOW_READ_ERROR_DESCRIPTION,
  FLOW_READ_ERROR_ESCAPE_ACTION,
  FLOW_READ_ERROR_MESSAGE,
  isGuidesView,
  isTerminallyBlocked,
  nexusEntryPointFor,
  offersNexusButton,
  useCloudflareBeta,
  useGuidesBack,
} from '../../../../cloudflare-integration';
import { forgetProbedDomain, rememberProbedDomain } from '../../../../cloudflare-integration/cfResumeDomainStore';
import { useCfResumeDomain } from '../../../../cloudflare-integration/useCfResumeDomain';
import { StepCodes } from '../../../steps';
import { useCfResumeFlow } from '../../../../cloudflare-integration/useCfResumeFlow';
import { useSetDomainMutation } from '../../../api/integrationWizardSlice';

const DEFAULT_INTEGRATION = defaultIntegrationMethods[0];

// FIXME: no design token for the list widths.
const LIST_WIDTH = 320;
const COLLAPSED_LIST_WIDTH = 80;

const NON_RESUMABLE_SCREENS = ['blocked', 'abandoned'];

export const INTEGRATION_GUIDE_LABEL = 'Integration guide';

const IntegrationStep = ({ onGoBack, onGoNext, jumpToStep, probedThisPage = domainsProbedThisPage }) => {
  const prerenderUser = usePrerenderUser();
  const location = useLocation();
  const { track } = useEvent();
  const [selectedOption, setSelectedOption] = useState(DEFAULT_INTEGRATION);
  const [searchQuery, setSearchQuery] = useState('');
  const [isListCollapsed, setIsListCollapsed] = useState(false);
  const [isNexusOpen, setIsNexusOpen] = useState(false);
  const { token } = theme.useToken();
  const scrollableRef = useRef(null);
  const { state, openChat, closeChat, sendMessage } = useNexusConversation();
  const isCloudflareBeta = useCloudflareBeta();
  const [startedOver, setStartedOver] = useState(false);
  const cloudflareDomainKey = useCfResumeDomain(isCloudflareBeta && !startedOver);
  const {
    flow: resumedFlow,
    isLoading: resumedFlowLoading,
    isUnavailable: resumedFlowUnavailable,
  } = useCfResumeFlow(isCloudflareBeta, cloudflareDomainKey);
  const [cloudflareProbe, setCloudflareProbe] = useState(null);
  const [recommendationAccepted, setRecommendationAccepted] = useState(false);
  const [nexusPrefill, setNexusPrefill] = useState(undefined);
  const [flowScene, setFlowScene] = useState(null);
  const prerenderUserId = usePrerenderUserId();
  const { setNarrowed } = useWizardGutter();
  const [saveWizardDomain] = useSetDomainMutation();

  const showsCloudflareFlow = isCloudflareBeta && !isGuidesView(location.search);
  const { goBack } = useGuidesBack(isCloudflareBeta, onGoBack);

  React.useEffect(() => {
    setNarrowed(isNexusOpen);

    return () => setNarrowed(false);
  }, [isNexusOpen, setNarrowed]);

  // The URL can name the domain key too, which `forgetProbedDomain` cannot clear.
  const startOver = () => {
    forgetProbedDomain(prerenderUserId);
    setStartedOver(true);
    setCloudflareProbe(null);
    setRecommendationAccepted(false);
  };

  const onCloudflareFinished = () => jumpToStep(StepCodes.StartRendering);

  const onProbed = (probe) => {
    setCloudflareProbe(probe);

    if (probe.flowOpened && probe.resumeScreen === null) {
      probedThisPage.mark(probe.domain);
    }

    if (probe.flowOpened && probe.resumeScreen !== null && !isTerminallyBlocked(probe)) {
      rememberProbedDomain(prerenderUserId, probe.domain);
    }
  };

  const onRecommendationAccepted = () => {
    rememberProbedDomain(prerenderUserId, cloudflareProbe.domain);
    setRecommendationAccepted(true);
  };

  const backToRecommendation = () => {
    forgetProbedDomain(prerenderUserId);
    setRecommendationAccepted(false);
  };

  const docContent = useMemo(() => {
    const doc = getDocument(selectedOption);
    return doc?.html;
  }, [selectedOption]);

  const matchingIntegrations = useMemo(() => filterIntegrations(defaultIntegrationMethods, searchQuery), [searchQuery]);

  const onToggleList = () => {
    setSearchQuery('');
    setIsListCollapsed((collapsed) => !collapsed);
  };

  const onNexusButtonClick = React.useCallback(
    async (referralCode, { newConversation = false, cfOpening } = {}) => {
      const entryPoint = referralCode || INTEGRATE_STEP_ENTRY_POINT;

      track('nexus_opened', {
        nexus_entry_point: entryPoint,
        ...(cfOpening && cfNexusOpenedProperties(cfOpening, prerenderUserId)),
      });

      setIsNexusOpen(true);
      await openChat(entryPoint, undefined, { newConversation });
    },
    [track, openChat, prerenderUserId]
  );

  const onOpenCloudflareNexus = React.useCallback(
    async (opening = {}) => {
      const { prefillText, errorShown } = opening;
      const summary = typeof prefillText === 'string' && prefillText !== '' ? prefillText : undefined;
      const entryPoint = opening.entryPoint ?? nexusEntryPointFor(errorShown);

      track('nexus_opened', {
        // The manual verify screen sends `verify_step` too.
        nexus_entry_point: entryPoint,
        ...cfNexusOpenedProperties(opening, prerenderUserId),
      });

      setNexusPrefill(summary);
      setIsNexusOpen(true);
      await openChat(entryPoint, AUTOMATED_CF_INTEGRATION_FLOW, { cfContext: cfStartContextOf(opening) });
    },
    [track, openChat, prerenderUserId]
  );

  const closeNexus = () => {
    setIsNexusOpen(false);
    closeChat();
  };

  const onChangeGuide = (newOption) => {
    setSelectedOption(newOption);
    if (scrollableRef.current) {
      scrollableRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const onContinueClick = () => {
    track('Integration Wizard: Integration method selected', {
      integration_method: selectedOption,
      subscription_plan: prerenderUser.chargebeePlanId,
    });
    onGoNext();
  };

  React.useEffect(() => {
    track('Integration: Integration Step Viewed');
  }, []);

  useNexusReferralHandoff(onNexusButtonClick, isNexusOpen);

  // Saved first: the Verify step pre-fills from the wizard's stored domain.
  const verifyIntegrationFor = async (domain) => {
    await saveWizardDomain(domain);
    jumpToStep(StepCodes.VerifyIntegration);
  };

  if (showsCloudflareFlow) {
    const showsBlocked = isTerminallyBlocked(cloudflareProbe);
    const showsRecommendation =
      !showsBlocked && cloudflareProbe?.flowOpened && cloudflareProbe.resumeScreen === null && !recommendationAccepted;
    // The server's `screen`, not this browser's state, so the email resumes on any device.
    const resumesFlow = resumedFlow && !NON_RESUMABLE_SCREENS.includes(resumedFlow.screen);
    // Which probes open a flow is the server's decision, not a list kept here.
    const flowDomain =
      showsBlocked || showsRecommendation
        ? null
        : cloudflareProbe?.flowOpened
          ? cloudflareProbe.domain
          : resumesFlow
            ? cloudflareDomainKey
            : null;

    const cloudflareScreen = () => {
      if (showsBlocked) {
        return (
          <CloudflareBlockedStep
            blockReason={cloudflareProbe.blockReason}
            domain={cloudflareProbe.domain}
            onGoBack={startOver}
          />
        );
      }

      if (showsRecommendation) {
        return (
          <CloudflareRecommendationStep
            domain={cloudflareProbe.domain}
            subCase={cloudflareProbe.subCase}
            onSetUp={onRecommendationAccepted}
            onGoBack={startOver}
          />
        );
      }

      if (flowDomain) {
        return (
          <CloudflareFlowScreens
            domain={flowDomain}
            onGoBack={recommendationAccepted ? backToRecommendation : startOver}
            onFinish={onCloudflareFinished}
            onOpenNexus={onOpenCloudflareNexus}
            onSceneChange={setFlowScene}
            probedThisPage={probedThisPage}
          />
        );
      }

      if (cloudflareDomainKey && resumedFlowLoading) {
        return <SpinningWheel matchParent />;
      }

      if (cloudflareDomainKey && resumedFlowUnavailable) {
        return (
          <Alert
            type="error"
            showIcon
            message={FLOW_READ_ERROR_MESSAGE}
            description={FLOW_READ_ERROR_DESCRIPTION}
            action={
              <Button size="small" onClick={startOver}>
                {FLOW_READ_ERROR_ESCAPE_ACTION}
              </Button>
            }
          />
        );
      }

      return (
        <CloudflareEntryStep
          onGoBack={onGoBack}
          onProbed={onProbed}
          onVerifyIntegration={verifyIntegrationFor}
        />
      );
    };

    return (
      <NexusDock
        fitsContent
        isOpen={isNexusOpen}
        offersButton={flowDomain !== null && offersNexusButton(flowScene?.screen ?? null)}
        onOpen={() =>
          onOpenCloudflareNexus(
            flowScene
              ? {
                  ...(flowScene.step && { fromStep: flowScene.step }),
                  domain: flowDomain,
                  subject: flowScene.subject,
                  errorShown: flowScene.errorShown,
                  ...(flowScene.prefillText && { prefillText: flowScene.prefillText }),
                }
              : {}
          )
        }
        pane={<NexusChat state={state} onSendMessage={sendMessage} prefillText={nexusPrefill} onClose={closeNexus} />}
      >
        {cloudflareScreen()}
      </NexusDock>
    );
  }

  return (
    <NexusDock
      isOpen={isNexusOpen}
      offersButton
      onOpen={() => onNexusButtonClick()}
      pane={<NexusChat state={state} onSendMessage={sendMessage} onClose={closeNexus} />}
    >
      <Flex vertical gap="large" flex={1} justify="center" style={{ minWidth: 0 }}>
        <IntegrationStepHeader />

        <Flex flex={1} style={{ width: '100%', position: 'relative' }}>
          <div
            ref={scrollableRef}
            style={{ position: 'absolute', inset: 0, overflowY: 'auto', scrollbarWidth: 'thin' }}
          >
            <Flex gap={token.margin} align="flex-start">
              <Flex
                vertical
                style={{
                  width: isListCollapsed ? COLLAPSED_LIST_WIDTH : LIST_WIDTH,
                  flexShrink: 0,
                  alignSelf: 'stretch',
                }}
              >
                <Flex
                  gap={token.marginXS}
                  align="center"
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    paddingBottom: token.margin,
                    backgroundColor: token.colorBgLayout,
                  }}
                >
                  <IntegrationListToggle collapsed={isListCollapsed} onToggle={onToggleList} />
                  <Flex flex={1}>
                    <IntegrationSearch
                      query={searchQuery}
                      onQueryChange={setSearchQuery}
                      collapsed={isListCollapsed}
                      onExpand={() => setIsListCollapsed(false)}
                    />
                  </Flex>
                </Flex>

                {isListCollapsed ? (
                  <Integrations
                    selectedMethod={selectedOption}
                    setSelectedMethod={onChangeGuide}
                    integrations={matchingIntegrations}
                    collapsed
                  />
                ) : matchingIntegrations.length > 0 ? (
                  <Integrations
                    selectedMethod={selectedOption}
                    setSelectedMethod={onChangeGuide}
                    integrations={matchingIntegrations}
                  />
                ) : (
                  <Empty
                    image={null}
                    imageStyle={{ display: 'none' }}
                    description={`No integration matches “${searchQuery.trim()}”`}
                  />
                )}
              </Flex>

              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: token.paddingXL,
                  backgroundColor: token.colorBgContainer,
                  border: `${token.lineWidth}px solid ${token.colorBorderSecondary}`,
                  borderRadius: token.borderRadiusLG,
                }}
              >
                {!docContent && <SpinningWheel matchParent />}
                {docContent && (
                  <div
                    className="documentation"
                    role="article"
                    aria-label={INTEGRATION_GUIDE_LABEL}
                    dangerouslySetInnerHTML={{ __html: docContent }}
                  ></div>
                )}
              </div>
            </Flex>
          </div>
        </Flex>

        {/* Not `BottomActionsContainer`: it centres the primary action; this design splits them to the edges. */}
        <Flex justify="space-between" align="center">
          {goBack ? (
            // `link` for the primary green: the theme overrides `colorLink` for buttons only.
            <Button onClick={goBack} icon={<LeftOutlined />} type="link">
              Back
            </Button>
          ) : (
            <span />
          )}
          <Button type="primary" onClick={onContinueClick}>
            Verify Integration
          </Button>
        </Flex>
      </Flex>
    </NexusDock>
  );
};

export default IntegrationStep;
